import React, {
  forwardRef,
  useEffect,
  useState,
  type IframeHTMLAttributes,
  type Ref,
  type SyntheticEvent,
} from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import cn from "classnames";
import styles from "./iframe.module.scss";
import { IFrameError } from "./iframe-error";
import LoadingSpinner from "core/widgets/loadingSpinnerWidget";

/**
 * Props for `IFrame.Query`.
 *
 * - Uses `query.data` as the iframe `src`.
 * - Shows app `<LoadingSpinner />` by default while fetching & until iframe `onLoad`.
 * - Shows `<IFrame.Error error={error} />` by default on query failure.
 * - You can override both via `loadingContent` and `renderError`.
 * - `className` styles the **container**; `frameClassName` styles the `<iframe>`;
 *   `overlayClassName` styles the overlay wrapper that holds loading/error content.
 */
export type IFrameQueryProps = Omit<
  IframeHTMLAttributes<HTMLIFrameElement>,
  "src" | "className"
> & {
  /**
   * React Query result whose `data` provides the iframe `src` URL (string).
   *
   * We use `isLoading`, `isFetching`, `isError`, `error`, and `data`.
   */
  query: UseQueryResult<string, unknown>;

  /**
   * Optional: override loading UI for this usage (defaults to `<LoadingSpinner />`).
   *
   * Pass `null` to render nothing while loading.
   */
  loadingContent?: React.ReactNode | null;

  /**
   * Optional: override error UI for this usage (defaults to `<IFrame.Error />`).
   *
   * Receives `query.error`. Pass `() => null` to render nothing on error.
   */
  renderError?: (error: unknown) => React.ReactNode;

  /** Container class for the wrapper `<div>`. */
  className?: string;

  /** Class for the `<iframe>` element. */
  frameClassName?: string;

  /** Class for the overlay wrapper that holds loading/error content. */
  overlayClassName?: string;
};

/** React Query–aware iframe component. */
export const IFrameQuery = forwardRef<HTMLIFrameElement, IFrameQueryProps>(
  (
    {
      query,
      loadingContent,
      renderError,
      className,
      frameClassName,
      overlayClassName,
      onLoad,
      onError,
      ...rest
    },
    ref: Ref<HTMLIFrameElement>,
  ) => {
    const { data: src, isLoading, isFetching, isError, error } = query;

    // Track when the iframe itself has finished loading
    const [iframeLoaded, setIFrameLoaded] = useState(false);

    // Reset when src changes
    useEffect(() => {
      setIFrameLoaded(false);
    }, [src]);

    const defaultLoading = <LoadingSpinner />;
    const LoadingUI =
      loadingContent === null ? null : (loadingContent ?? defaultLoading);

    const defaultRenderError = (err: unknown) => <IFrameError error={err} />;
    const renderErrorUI = renderError ?? defaultRenderError;

    const showLoading =
      !!LoadingUI &&
      (isLoading || isFetching || (!!src && !iframeLoaded && !isError));

    const showError = !!renderErrorUI && isError && !src;

    const handleLoad = (e: SyntheticEvent<HTMLIFrameElement, Event>) => {
      setIFrameLoaded(true);
      onLoad?.(e);
    };

    const handleError = (e: SyntheticEvent<HTMLIFrameElement, Event>) => {
      onError?.(e);
    };

    return (
      <div className={cn(styles.container, className)}>
        {showLoading && (
          <div
            role="status"
            aria-live="polite"
            className={cn(styles.overlay, overlayClassName)}
          >
            {LoadingUI}
          </div>
        )}

        {showError && (
          <div
            role="alert"
            aria-live="assertive"
            className={cn(styles.overlay, overlayClassName)}
          >
            {renderErrorUI(error)}
          </div>
        )}

        {src && !isError && (
          <iframe
            ref={ref}
            src={src}
            onLoad={handleLoad}
            onError={handleError}
            className={cn(styles.frame, frameClassName)}
            {...rest}
          />
        )}
      </div>
    );
  },
);

IFrameQuery.displayName = "IFrame.Query";
